import { type ReactNode, useId, useState } from "react";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ModalContent } from "@/components/redesign/ModalContent";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface PracticeSetupDialogProps {
  allowInstantFeedback: boolean;
  altStartButtonText?: string;
  children: ReactNode;
  coverageLabels?: readonly string[];
  description?: string;
  feedbackDescription?: string;
  feedbackTitle?: string;
  icon?: string;
  locked: boolean;
  lockedActionLabel?: string;
  onInstantFeedbackChange: () => void;
  onStart: (alternate: boolean) => void;
  practiceLengthLabel?: string;
  showFeedbackToggle: boolean;
  singleLengthTitle: string;
  startButtonText: string;
  startLabel?: string;
  title: string;
}

export function hasSelectablePracticeLengths(
  altStartButtonText: string | undefined,
) {
  return Boolean(altStartButtonText);
}

export function parsePracticeLengthOption(label: string) {
  const match = label.match(/^(.+?)\s*\((.+)\)$/);

  return match
    ? { detail: match[2], title: match[1] }
    : { detail: undefined, title: label };
}

export function getPrimaryPracticeActionLabel({
  locked,
  lockedActionLabel,
  resolvedStartLabel,
  startButtonText,
}: Pick<
  PracticeSetupDialogProps,
  "locked" | "lockedActionLabel" | "startButtonText"
> & { resolvedStartLabel: string }) {
  return locked ? lockedActionLabel || startButtonText : resolvedStartLabel;
}

export function PracticeSetupDialog({
  allowInstantFeedback,
  altStartButtonText,
  children,
  coverageLabels,
  description,
  feedbackDescription,
  feedbackTitle,
  icon,
  locked,
  lockedActionLabel,
  onInstantFeedbackChange,
  onStart,
  practiceLengthLabel,
  showFeedbackToggle,
  singleLengthTitle,
  startButtonText,
  startLabel,
  title,
}: PracticeSetupDialogProps) {
  const { t } = useTranslation();
  const resolvedFeedbackDescription = feedbackDescription || t("study.show_answers_description");
  const resolvedFeedbackTitle = feedbackTitle || t("study.show_answers");
  const resolvedStartLabel = startLabel || t("study.start_practice");
  const [useShortLength, setUseShortLength] = useState(false);
  const practiceLengthGroupName = useId();
  const hasLengthOptions = hasSelectablePracticeLengths(altStartButtonText);
  const regularLengthOption = parsePracticeLengthOption(startButtonText);
  const shortLengthOption = altStartButtonText
    ? parsePracticeLengthOption(altStartButtonText)
    : undefined;
  const primaryActionLabel = getPrimaryPracticeActionLabel({
    locked,
    lockedActionLabel,
    resolvedStartLabel,
    startButtonText,
  });
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <ModalContent
        className="w-[560px] max-w-[calc(100vw-32px)]"
        footer={
          <div>
            <button
              className="dp-button-primary h-14 w-full px-8 text-lg"
              onClick={() => onStart(hasLengthOptions && useShortLength)}
              type="button"
            >
              {primaryActionLabel}
            </button>
          </div>
        }
        icon={icon ? <img alt="" className="h-12 w-12" src={icon} /> : null}
        title={title}
      >
        <div className="flex flex-col gap-4">
          <p className="whitespace-pre-wrap text-sm leading-[21px] tracking-[0.07px] text-[#0a0c11]">{description}</p>
          {coverageLabels && coverageLabels.length > 0 && (
            <section>
              <p className="mb-2 text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">{t("study.what_it_covers")}</p>
              <div className="flex flex-wrap gap-2">
                {coverageLabels.map((label) => (
                  <span className="rounded-full border border-[#dddfe4] bg-[#f4f4f6] px-4 py-2 text-sm font-medium leading-[21px] tracking-[0.07px] text-[#5b616d]" key={label}>
                    {label}
                  </span>
                ))}
              </div>
            </section>
          )}
          <section>
            <p className="mb-2 text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">{t("study.practice_length")}</p>
            {hasLengthOptions ? (
              <fieldset className="grid grid-cols-2 gap-3">
                <legend className="sr-only">{t("study.practice_length")}</legend>
                <label
                  className={cn(
                    "flex cursor-pointer flex-col items-start gap-1.5 rounded-2xl border-[1.5px] px-4 py-3 text-left outline-none transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--dp-brand-button)] has-[:focus-visible]:ring-offset-2",
                    !useShortLength
                      ? "bg-[var(--dp-brand-soft)] text-[#0a0c11]"
                      : "border-[#dddfe4] bg-white text-[#5b616d] hover:bg-[#f4f4f6]",
                  )}
                  style={
                    !useShortLength
                      ? {
                          borderColor:
                            "color-mix(in srgb, var(--dp-brand-button) 50%, transparent)",
                        }
                      : undefined
                  }
                >
                  <input
                    checked={!useShortLength}
                    className="sr-only"
                    name={practiceLengthGroupName}
                    onChange={() => setUseShortLength(false)}
                    type="radio"
                  />
                  <span className="text-base font-semibold leading-6">
                    {regularLengthOption.title}
                  </span>
                  {regularLengthOption.detail && (
                    <span className="text-sm font-medium leading-[21px] tracking-[0.07px] text-[#5b616d]">
                      {regularLengthOption.detail}
                    </span>
                  )}
                </label>
                <label
                  className={cn(
                    "flex cursor-pointer flex-col items-start gap-1.5 rounded-2xl border-[1.5px] px-4 py-3 text-left outline-none transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--dp-brand-button)] has-[:focus-visible]:ring-offset-2",
                    useShortLength
                      ? "bg-[var(--dp-brand-soft)] text-[#0a0c11]"
                      : "border-[#dddfe4] bg-white text-[#5b616d] hover:bg-[#f4f4f6]",
                  )}
                  style={
                    useShortLength
                      ? {
                          borderColor:
                            "color-mix(in srgb, var(--dp-brand-button) 50%, transparent)",
                        }
                      : undefined
                  }
                >
                  <input
                    checked={useShortLength}
                    className="sr-only"
                    name={practiceLengthGroupName}
                    onChange={() => setUseShortLength(true)}
                    type="radio"
                  />
                  <span className="text-base font-semibold leading-6">
                    {shortLengthOption?.title}
                  </span>
                  {shortLengthOption?.detail && (
                    <span className="text-sm font-medium leading-[21px] tracking-[0.07px] text-[#5b616d]">
                      {shortLengthOption.detail}
                    </span>
                  )}
                </label>
              </fieldset>
            ) : (
              <div
                className="flex flex-col items-start gap-1.5 rounded-2xl border-[1.5px] bg-[var(--dp-brand-soft)] px-4 py-3"
                style={{
                  borderColor:
                    "color-mix(in srgb, var(--dp-brand-button) 50%, transparent)",
                }}
              >
                <p className="font-semibold leading-6 text-[#0a0c11]">{singleLengthTitle}</p>
                <p className="text-sm font-medium leading-[21px] tracking-[0.07px] text-[#5b616d]">{practiceLengthLabel || startButtonText}</p>
              </div>
            )}
          </section>
          {showFeedbackToggle && (
            <label className="flex items-center gap-4 rounded-2xl bg-[#f4f4f6] p-4">
              <span className="min-w-0 flex-1">
                <span className="block font-semibold leading-6 text-[#0a0c11]">{resolvedFeedbackTitle}</span>
                <span className="block text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">{resolvedFeedbackDescription}</span>
              </span>
              <Switch
                checked={allowInstantFeedback}
                className="h-[26px] w-[46px] data-[state=checked]:bg-[var(--dp-brand-button)] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-5"
                onCheckedChange={onInstantFeedbackChange}
              />
            </label>
          )}
        </div>
      </ModalContent>
    </Dialog>
  );
}
